import { Controller } from "@hotwired/stimulus"

const TI_SEVERITY_CLASS = { critical: "siem-ti-critical", high: "siem-ti-high", medium: "siem-ti-medium", low: "siem-ti-low" }

const PROVIDER_INFO = {
  "Microsoft-Windows-Security": {
    description: "Authentication, authorization, account management, privilege use, and policy changes on Windows systems.",
    eventIds: [
      { id: "4624", desc: "Successful logon" },
      { id: "4625", desc: "Failed logon" },
      { id: "4634", desc: "Account logoff" },
      { id: "4648", desc: "Logon with explicit credentials (runas)" },
      { id: "4688", desc: "Process created (requires audit policy)" },
      { id: "4720", desc: "User account created" },
      { id: "4728", desc: "Member added to security group" },
      { id: "4768", desc: "Kerberos TGT requested" },
      { id: "4769", desc: "Kerberos service ticket requested" },
      { id: "4771", desc: "Kerberos pre-auth failed" },
      { id: "4776", desc: "NTLM credential validation attempt" },
    ],
    huntFor: [
      "Brute force: 4625 spike from a single source IP or user",
      "Pass-the-hash: 4624 type 3 without preceding 4648",
      "Kerberoasting: 4769 with RC4 (0x17) encryption type",
      "AS-REP roasting: 4768 with pre-authentication disabled",
    ],
  },
  "Microsoft-Windows-Sysmon": {
    description: "Deep endpoint telemetry from the Sysinternals System Monitor driver — process, network, file, registry, and DNS.",
    eventIds: [
      { id: "1",  desc: "Process created (hash + command line)" },
      { id: "3",  desc: "Network connection established" },
      { id: "7",  desc: "Image / DLL loaded" },
      { id: "8",  desc: "CreateRemoteThread detected" },
      { id: "10", desc: "Process accessed another process" },
      { id: "11", desc: "File created" },
      { id: "12", desc: "Registry key created or deleted" },
      { id: "13", desc: "Registry value set" },
      { id: "22", desc: "DNS query made" },
      { id: "25", desc: "Process tampering (hollowing / herpaderping)" },
    ],
    huntFor: [
      "LSASS dumping: Event 10 targeting lsass.exe",
      "Lateral movement: Event 3 from wscript/mshta/unexpected processes",
      "Living-off-the-land: Event 1 spawned from Office / browser",
      "Process injection: Event 8 CreateRemoteThread across processes",
    ],
  },
  "Microsoft-Windows-PowerShell": {
    description: "PowerShell script block, module, and pipeline execution logging. Captures executed code before obfuscation is applied.",
    eventIds: [
      { id: "4103", desc: "Module logging — pipeline execution details" },
      { id: "4104", desc: "Script block logging — actual executed code" },
    ],
    huntFor: [
      "Encoded commands: base64 in ScriptBlockText",
      "Download cradles: Invoke-Expression + WebClient / WebRequest",
      "AMSI bypass: AmsiUtils / amsiInitFailed string references",
      "In-memory loading: Reflection.Assembly.Load / LoadFile",
    ],
  },
  "Microsoft-Windows-TaskScheduler": {
    description: "Scheduled task registration, modification, deletion, and execution events.",
    eventIds: [
      { id: "106", desc: "Task registered" },
      { id: "140", desc: "Task updated" },
      { id: "141", desc: "Task deleted" },
      { id: "200", desc: "Task action launched" },
      { id: "201", desc: "Task action completed" },
    ],
    huntFor: [
      "Persistence: new tasks with unusual authors or paths",
      "Lateral movement: tasks registered on remote systems",
    ],
  },
  "Microsoft-Windows-WMI-Activity": {
    description: "WMI queries, remote operations, and event subscriptions — common lateral movement and persistence vector.",
    eventIds: [
      { id: "5857", desc: "WMI provider loaded" },
      { id: "5860", desc: "Temporary event subscription registered" },
      { id: "5861", desc: "Permanent event subscription (persistence)" },
    ],
    huntFor: [
      "Persistence: Event 5861 permanent subscription created",
      "Lateral execution: WMI invoked from unexpected parent processes",
    ],
  },
  "huntress-edr": {
    description: "Huntress EDR agent telemetry — process, file, network events, and threat detections from the Huntress platform.",
    eventIds: [],
    huntFor: [
      "Priority: events where severity == critical",
      "Process lineage: correlate by ProcessGuid or ParentProcessId",
    ],
  },
}

export default class extends Controller {
  static targets = ["filterInput", "field", "fieldValues"]
  static values = { fieldValuesUrl: String, threatIntelUrl: String }

  #infoPopover = null
  #boundCloseInfo = null

  connect() {
    this.#loadSourceVolume()
  }

  filterFields() {
    const filter = this.filterInputTarget.value.toLowerCase().trim()
    this.fieldTargets.forEach(field => {
      const name = (field.dataset.fieldName || "").toLowerCase()
      field.classList.toggle("hidden", filter.length > 0 && !name.includes(filter))
    })
  }

  async toggleValues(event) {
    const fieldEl = event.target.closest("[data-field-name]")
    if (!fieldEl) return

    const fieldName = fieldEl.dataset.fieldName
    const valuesEl = this.fieldValuesTargets.find(el => el.dataset.fieldName === fieldName)
    if (!valuesEl) return

    if (valuesEl.classList.contains("hidden")) {
      valuesEl.classList.remove("hidden")
      if (!valuesEl.dataset.loaded) {
        await this.#loadFieldValues(fieldName, valuesEl)
      }
    } else {
      valuesEl.classList.add("hidden")
    }
  }

  filterByField(event) {
    event.stopPropagation()
    const fieldName = event.currentTarget.dataset.fieldName
    if (!fieldName) return

    const textarea = document.querySelector("textarea[name='query']")
    if (!textarea) return

    const clause = `| where ${fieldName} == ""`
    const updated = this.#withFromLogs(textarea.value, clause)
    const cursorIndex = updated.length - 1

    const editor = this.application.getControllerForElementAndIdentifier(textarea, "accounts--logs--esql-editor")
    if (editor) {
      editor.setValue(updated, { cursorIndex, focus: true })
    } else {
      textarea.value = updated
      textarea.dispatchEvent(new Event("input", { bubbles: true }))
      textarea.focus()
      textarea.setSelectionRange(cursorIndex, cursorIndex)
    }
  }

  topValues(event) {
    event.stopPropagation()
    const fieldName = event.currentTarget.dataset.fieldName
    if (!fieldName) return
    const textarea = document.querySelector("textarea[name='query']")
    if (!textarea) return
    textarea.value = `FROM logs\n| STATS count = COUNT(*) BY ${fieldName}\n| SORT count DESC\n| LIMIT 10`
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
    this.#submitSearch()
  }

  addColumn(event) {
    event.stopPropagation()
    const fieldName = event.currentTarget.dataset.fieldName
    if (!fieldName) return

    const textarea = document.querySelector("textarea[name='query']")
    if (!textarea) return

    let query = textarea.value.trim()
    const keepMatch = query.match(/(\|\s*keep\s+)([^|]+)/i)
    if (keepMatch) {
      const fields = keepMatch[2].trim().split(/\s*,\s*/)
      if (!fields.includes(fieldName)) {
        query = query.replace(keepMatch[0], keepMatch[1] + [...fields, fieldName].join(", "))
      }
    } else {
      query = query ? `${query}\n| keep ${fieldName}` : `from logs\n| keep ${fieldName}`
    }

    textarea.value = query
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
    this.#submitSearch()
  }

  filterByValue(event) {
    const { fieldName, fieldValue } = event.currentTarget.dataset
    if (!fieldName || fieldValue === undefined) return
    this.#appendToQuery(`| where ${fieldName} == "${fieldValue.replace(/"/g, '\\"')}"`)
    this.#submitSearch()
  }

  showProviderInfo(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const name = btn.dataset.providerName
    if (!name) return

    if (this.#infoPopover) {
      this.#destroyInfoPopover()
      return
    }

    const info = PROVIDER_INFO[name]
    if (!info) {
      const pop = document.createElement("div")
      pop.className = "siem-provider-popover"
      pop.innerHTML = `<div class="siem-provider-popover-name">${this.#esc(name)}</div>
        <div class="siem-provider-popover-desc">No reference data available for this source.</div>`
      this.#mountPopover(pop, btn)
      return
    }

    const eventIdRows = info.eventIds.map(e =>
      `<div class="siem-provider-event-id">
        <span class="siem-event-id-badge">${this.#esc(e.id)}</span>
        <span class="siem-event-id-desc">${this.#esc(e.desc)}</span>
      </div>`
    ).join("")

    const huntRows = info.huntFor.map(h =>
      `<div class="siem-provider-hunt-item">${this.#esc(h)}</div>`
    ).join("")

    const pop = document.createElement("div")
    pop.className = "siem-provider-popover"
    pop.innerHTML = `
      <div class="siem-provider-popover-name">${this.#esc(name)}</div>
      <div class="siem-provider-popover-desc">${this.#esc(info.description)}</div>
      ${info.eventIds.length ? `<div class="siem-provider-popover-section">Key Event IDs</div>${eventIdRows}` : ""}
      ${info.huntFor.length ? `<div class="siem-provider-popover-section">Hunt For</div>${huntRows}` : ""}
    `
    this.#mountPopover(pop, btn)
  }

  #mountPopover(pop, anchor) {
    document.body.appendChild(pop)
    this.#infoPopover = pop

    const rect = anchor.getBoundingClientRect()
    const pw = pop.offsetWidth || 300
    const ph = pop.offsetHeight || 200
    const left = Math.min(rect.right + 8, window.innerWidth - pw - 8)
    const top  = Math.min(rect.top, window.innerHeight - ph - 8)
    pop.style.left = `${left}px`
    pop.style.top  = `${top + window.scrollY}px`

    this.#boundCloseInfo = e => {
      if (!pop.contains(e.target) && e.target !== anchor) this.#destroyInfoPopover()
    }
    setTimeout(() => document.addEventListener("click", this.#boundCloseInfo), 0)
  }

  #destroyInfoPopover() {
    this.#infoPopover?.remove()
    this.#infoPopover = null
    if (this.#boundCloseInfo) {
      document.removeEventListener("click", this.#boundCloseInfo)
      this.#boundCloseInfo = null
    }
  }

  async #loadSourceVolume() {
    if (!this.hasFieldValuesUrlValue) return
    const container = this.element.querySelector(".siem-source-volume-rows")
    if (!container) return

    try {
      const url = new URL(this.fieldValuesUrlValue, window.location.origin)
      url.searchParams.set("field", "event.provider")
      const timeRange = new URLSearchParams(window.location.search).get("time_range")
      if (timeRange) url.searchParams.set("time_range", timeRange)

      const resp = await fetch(url.toString(), { headers: { Accept: "application/json" } })
      if (!resp.ok) return
      const data = await resp.json()
      if (!data.length) return

      const max = data[0].count
      container.innerHTML = data.map(({ value, count }) => {
        const pct = ((count / max) * 100).toFixed(1)
        const safe = this.#esc(value || "unknown")
        return `<div class="siem-sv-row" role="button" tabindex="0" data-provider="${safe}">
          <span class="siem-sv-name" title="${safe}">${safe}</span>
          <div class="siem-sv-bar-wrap"><div class="siem-sv-bar" style="width:${pct}%"></div></div>
          <span class="siem-sv-count">${Number(count).toLocaleString()}</span>
        </div>`
      }).join("")

      container.querySelectorAll(".siem-sv-row").forEach(row => {
        row.addEventListener("click", () => {
          const provider = row.dataset.provider
          if (!provider) return
          this.#appendToQuery(`| where event.provider == "${provider.replace(/"/g, '\\"')}"`)
          this.#submitSearch()
        })
      })

      this.element.querySelector(".siem-source-volume")?.classList.remove("hidden")
    } catch { /* silent */ }
  }

  async #loadFieldValues(fieldName, container) {
    try {
      const url = new URL(this.fieldValuesUrlValue, window.location.origin)
      url.searchParams.set("field", fieldName)

      const timeRange = new URLSearchParams(window.location.search).get("time_range")
      if (timeRange) url.searchParams.set("time_range", timeRange)

      const response = await fetch(url.toString(), {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      container.dataset.loaded = "true"

      if (data.length === 0) {
        container.innerHTML = '<p class="siem-field-values-empty">No values found in current time range</p>'
        return
      }

      container.innerHTML = data.map(({ value, count }) => {
        const safeValue = String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;")
        return `<div class="siem-field-value-row"
                     data-action="click->accounts--logs--field-browser#filterByValue"
                     data-field-name="${fieldName}"
                     data-field-value="${safeValue}">
                  <span class="siem-field-value-text" title="${safeValue}">${safeValue}</span>
                  <span class="siem-field-value-count">${Number(count).toLocaleString()}</span>
                  <span class="siem-ti-badge hidden" data-ti-value="${safeValue}"></span>
                </div>`
      }).join("")

      this.#enrichWithThreatIntel(fieldName, container)
    } catch (_err) {
      container.innerHTML = '<p class="siem-field-values-empty">Failed to load values</p>'
    }
  }

  #correlateFromTi(fieldName, value) {
    const v = value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')
    let query
    if (fieldName.includes("hash") || fieldName === "file.hash.sha256" || fieldName === "file.hash.md5") {
      query = `FROM logs | WHERE Hashes LIKE "%${v}%" | STATS count = COUNT(*), computers = COUNT_DISTINCT(Computer), users = COUNT_DISTINCT(SubjectUserName) BY EventID, ProcessName | SORT count DESC`
    } else {
      query = `FROM logs | WHERE ${fieldName} == "${v}" | SORT TimeCreated ASC`
    }
    const textarea = document.querySelector("textarea[name='query']")
    if (!textarea) return
    textarea.value = query
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
    this.#submitSearch()
  }

  #esc(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }

  #appendToQuery(clause) {
    const textarea = document.querySelector("textarea[name='query']")
    if (!textarea) return

    textarea.value = this.#withFromLogs(textarea.value, clause)

    // Sync the replicated value used for styling
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
  }

  #withFromLogs(currentValue, clause) {
    const current = (currentValue || "").trim()
    if (!current) return `FROM logs\n${clause}`
    if (/^\s*from\s+logs\b/i.test(current)) return `${current}\n${clause}`
    return `FROM logs\n${current}\n${clause}`
  }

  #submitSearch() {
    const form = document.querySelector("textarea[name='query']")?.closest("form")
    if (form) form.requestSubmit()
  }

  async #enrichWithThreatIntel(fieldName, container) {
    if (!this.hasThreatIntelUrlValue) return

    const rows = container.querySelectorAll(".siem-field-value-row")
    if (!rows.length) return

    // Only enrich IP, domain, and hash fields
    const enrichable = ["source.ip", "destination.ip", "dns.question.name", "url.domain",
      "destination.domain", "source.domain", "file.hash.md5", "file.hash.sha256"]
    if (!enrichable.includes(fieldName)) return

    const rowList = [...rows].filter(r => r.dataset.fieldValue)
    if (!rowList.length) return

    await Promise.all(rowList.map(async row => {
      const value = row.dataset.fieldValue
      try {
        const url = new URL(this.threatIntelUrlValue, window.location.origin)
        url.searchParams.set("value", value)
        const resp = await fetch(url.toString(), { headers: { Accept: "application/json" } })
        if (!resp.ok) return
        const matches = await resp.json()
        if (!matches.length) return

        const top = matches[0]
        const badge = row.querySelector(".siem-ti-badge")
        if (!badge) return
        badge.classList.remove("hidden")
        badge.classList.add(TI_SEVERITY_CLASS[top.severity] || "siem-ti-medium")
        badge.title = `${top.severity.toUpperCase()} — ${top.source_name}: ${top.description || "Known threat indicator"} · Click to correlate`
        badge.textContent = "⚠ TI"
        badge.style.cursor = "pointer"
        badge.addEventListener("click", e => {
          e.stopPropagation()
          this.#correlateFromTi(fieldName, value)
        })
      } catch { /* silent */ }
    }))
  }
}
